import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

// 3x3 sliding puzzle (the classic 8-puzzle). 0 is the blank. board[i] is the tile shown in grid
// cell i. Solved = 1..8 then blank. You slide a tile into the blank by DRAGGING it (or tapping) —
// the drag mirrors the piece-drag on the main chessboard, just for fun.
const SIZE = 3;
const CELL = 84; // px, kept in sync with .slide-board in site.css
const SOLVED = [1, 2, 3, 4, 5, 6, 7, 8, 0];

const rowOf = (i: number) => Math.floor(i / SIZE);
const colOf = (i: number) => i % SIZE;

// Adjacent-to-blank cells are the only movable ones. Returns the drag axis + direction toward the
// blank, or null when the cell can't move.
function slideInfo(i: number, blank: number): { axis: 'x' | 'y'; dir: 1 | -1 } | null {
  if (rowOf(i) === rowOf(blank) && Math.abs(colOf(i) - colOf(blank)) === 1) {
    return { axis: 'x', dir: colOf(blank) > colOf(i) ? 1 : -1 };
  }
  if (colOf(i) === colOf(blank) && Math.abs(rowOf(i) - rowOf(blank)) === 1) {
    return { axis: 'y', dir: rowOf(blank) > rowOf(i) ? 1 : -1 };
  }
  return null;
}

// Shuffle by walking the blank through random legal slides from the solved state, so the result is
// always solvable (a plain permutation shuffle can land on an unsolvable board).
function shuffled(): number[] {
  const board = SOLVED.slice();
  let blank = board.indexOf(0);
  let last = -1;
  for (let n = 0; n < 80; n++) {
    const neighbors: number[] = [];
    for (let i = 0; i < board.length; i++) {
      if (i !== last && slideInfo(i, blank) != null) {
        neighbors.push(i);
      }
    }
    const pick = neighbors[Math.floor(Math.random() * neighbors.length)];
    [board[pick], board[blank]] = [board[blank], board[pick]];
    last = blank;
    blank = pick;
  }
  return board.every((v, i) => v === SOLVED[i]) ? shuffled() : board;
}

export default function SlidePuzzle(): React.ReactNode {
  const [board, setBoard] = useState<number[]>(shuffled);
  const [moves, setMoves] = useState(0);
  const [drag, setDrag] = useState<{ index: number; axis: 'x' | 'y'; dir: 1 | -1; offset: number } | null>(
    null,
  );
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const blank = useMemo(() => board.indexOf(0), [board]);
  const solved = useMemo(() => board.every((v, i) => v === SOLVED[i]), [board]);

  const commit = useCallback(
    (index: number) => {
      setBoard((b) => {
        const bl = b.indexOf(0);
        if (slideInfo(index, bl) == null) {
          return b;
        }
        const next = b.slice();
        [next[index], next[bl]] = [next[bl], next[index]];
        return next;
      });
      setMoves((m) => m + 1);
    },
    [],
  );

  const onPointerDown = (e: React.PointerEvent, index: number) => {
    if (solved) return;
    const info = slideInfo(index, blank);
    if (info == null) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDrag({ index, axis: info.axis, dir: info.dir, offset: 0 });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (drag == null || startRef.current == null) return;
    const raw = drag.axis === 'x' ? e.clientX - startRef.current.x : e.clientY - startRef.current.y;
    const offset = Math.max(0, Math.min(CELL, raw * drag.dir));
    setDrag((d) => (d == null ? d : { ...d, offset }));
  };

  const endDrag = (e: React.PointerEvent) => {
    if (drag == null || startRef.current == null) {
      setDrag(null);
      return;
    }
    const moved = Math.abs(e.clientX - startRef.current.x) + Math.abs(e.clientY - startRef.current.y);
    // Committed if dragged past the halfway point, or a plain tap (barely moved).
    if (drag.offset > CELL / 2 || moved < 6) {
      commit(drag.index);
    }
    setDrag(null);
    startRef.current = null;
  };

  const reset = () => {
    setBoard(shuffled());
    setMoves(0);
    setDrag(null);
  };

  return (
    <div className="cm-mini">
      <div className={`cm-mini-status${solved ? ' cm-mini-status--win' : ''}`}>
        {solved ? `Solved in ${moves} moves! 🎉` : 'Drag a tile into the gap'}
      </div>
      <div className="slide-board" style={{ width: SIZE * CELL, height: SIZE * CELL }}>
        {board.map((v, i) => {
          if (v === 0) return null;
          const dragging = drag?.index === i;
          const tx = dragging && drag.axis === 'x' ? drag.dir * drag.offset : 0;
          const ty = dragging && drag.axis === 'y' ? drag.dir * drag.offset : 0;
          const movable = !solved && slideInfo(i, blank) != null;
          return (
            <div
              key={v}
              className={`slide-tile${movable ? ' slide-tile--movable' : ''}${dragging ? ' slide-tile--dragging' : ''}`}
              style={{
                left: colOf(i) * CELL,
                top: rowOf(i) * CELL,
                transform: `translate(${tx}px, ${ty}px)`,
              }}
              onPointerDown={(e) => onPointerDown(e, i)}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {v}
            </div>
          );
        })}
      </div>
      <div className="cm-mini-actions">
        <span className="cm-mini-meta">{moves} moves</span>
        <Button variant="secondary" size="sm" onClick={reset}>
          Shuffle
        </Button>
      </div>
    </div>
  );
}
